import { useState, useEffect, useCallback } from 'react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Receipt, MagnifyingGlass, ArrowClockwise, Calendar, CreditCard } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const fmt = (n) => '₹' + Math.round(Number(n || 0));

export default function CardAuditTrailPage() {
  const { user } = useAuth();
  const isCA = user?.role === 'accountant';
  const [rows, setRows] = useState([]);
  const [cardFilter, setCardFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (cardFilter) params.set('card_id', cardFilter);
    if (fromDate) params.set('from_date', fromDate);
    if (toDate) params.set('to_date', toDate);
    try {
      const r = await api.get(`/card-ledger/?${params.toString()}`);
      setRows(r.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Load failed');
    } finally { setLoading(false); }
  }, [cardFilter, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  const totals = rows.reduce((acc, r) => ({
    card_debit: acc.card_debit + r.card_debit,
    prebalance: acc.prebalance + r.prebalance_used,
    submitted: acc.submitted + r.submitted_total,
    rows: acc.rows + r.rows_count,
  }), { card_debit: 0, prebalance: 0, submitted: 0, rows: 0 });

  const exportExcel = () => {
    if (rows.length === 0) { toast.error('No entries'); return; }
    const headers = ['Date', 'Card', 'Distributor', 'Agency', 'Rows', 'Submitted', 'Pre-Balance', 'Card Debit', 'Before', 'After', 'Submitted By'];
    let html = '<html><head><meta charset="utf-8"></head><body><table border="1">';
    html += '<tr style="background:#305496;color:#fff;font-weight:bold">' + headers.map(h => `<th>${h}</th>`).join('') + '</tr>';
    rows.forEach(r => {
      const bg = r.card_debit > 0 ? '#FFEB9C' : '#C6EFCE';
      html += `<tr style="background:${bg}">` + [
        r.submitted_at ? new Date(r.submitted_at).toLocaleString('en-IN') : '',
        `****${r.card_last4}`, r.distributor_name, r.booking_agency, r.rows_count,
        r.submitted_total, r.prebalance_used, r.card_debit, r.before_balance, r.after_balance,
        r.submitted_by_name,
      ].map(c => `<td>${c ?? ''}</td>`).join('') + '</tr>';
    });
    html += '</table></body></html>';
    const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `CardAuditTrail_${Date.now()}.xls`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} entries`);
  };

  return (
    <div className="space-y-4" data-testid="card-audit-trail">
      <div className="flex items-center gap-2 flex-wrap">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Receipt size={26} weight="duotone" className="text-violet-600" /> Card Audit Trail
        </h1>
        <span className="text-sm text-gray-500">Permanent ledger — har submit ka card debit + pre-balance record</span>
        <Button variant="outline" size="sm" className="ml-auto" onClick={load}>
          <ArrowClockwise size={14} /> Refresh
        </Button>
        <Button size="sm" onClick={exportExcel} className="bg-emerald-600 hover:bg-emerald-700">
          📥 Export Excel
        </Button>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div className="bg-blue-50 border border-blue-200 rounded p-2 text-center">
          <div className="text-[10px] text-blue-700 uppercase">Submitted Total</div>
          <div className="text-xl font-bold text-blue-800">{fmt(totals.submitted)}</div>
          <div className="text-[10px] text-gray-500">{totals.rows} rows</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded p-2 text-center">
          <div className="text-[10px] text-amber-700 uppercase">Pre-Balance Used</div>
          <div className="text-xl font-bold text-amber-800">{fmt(totals.prebalance)}</div>
        </div>
        <div className="bg-violet-50 border border-violet-200 rounded p-2 text-center">
          <div className="text-[10px] text-violet-700 uppercase">Card Debited</div>
          <div className="text-xl font-bold text-violet-800">{fmt(totals.card_debit)}</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-center">
          <div className="text-[10px] text-emerald-700 uppercase">Submits</div>
          <div className="text-xl font-bold text-emerald-800">{rows.length}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border rounded p-3 flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[200px]">
          <div className="text-[10px] text-gray-500 mb-0.5"><CreditCard size={10} className="inline" /> Card ID</div>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2 top-2 text-gray-400" />
            <Input value={cardFilter} onChange={(e) => setCardFilter(e.target.value)} placeholder="Card ID (24-char hex)" className="pl-7 h-8 text-xs font-mono" data-testid="filter-card-id" />
          </div>
        </div>
        <div className="min-w-[140px]">
          <div className="text-[10px] text-gray-500 mb-0.5"><Calendar size={10} className="inline" /> From</div>
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-8 text-xs" />
        </div>
        <div className="min-w-[140px]">
          <div className="text-[10px] text-gray-500 mb-0.5">To</div>
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-8 text-xs" />
        </div>
        <Button size="sm" className="h-8" onClick={load}>Apply</Button>
      </div>

      {/* Table */}
      <div className="text-xs text-gray-600">{loading ? 'Loading…' : `${rows.length} ledger entries`}</div>
      <div className="overflow-x-auto bg-white border rounded">
        <table className="w-full text-xs">
          <thead className="bg-violet-50 text-[10px] uppercase text-violet-900">
            <tr>
              <th className="px-2 py-2 text-left">Submitted</th>
              <th className="px-2 py-2 text-left">Card</th>
              <th className="px-2 py-2 text-left">File / Agency</th>
              <th className="px-2 py-2 text-center">Rows</th>
              <th className="px-2 py-2 text-right">Submitted ₹</th>
              <th className="px-2 py-2 text-right">Pre-Bal Used</th>
              <th className="px-2 py-2 text-right">Card Debit</th>
              <th className="px-2 py-2 text-right">Before → After</th>
              {!isCA && <th className="px-2 py-2 text-left">Submitter</th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={isCA ? 8 : 9} className="text-center py-8 text-gray-400">{loading ? 'Loading…' : 'No ledger entries yet'}</td></tr>
            ) : rows.map(r => (
              <tr key={r.id} className="border-t hover:bg-violet-50/30" data-testid={`ledger-row-${r.id}`}>
                <td className="px-2 py-1">{r.submitted_at ? new Date(r.submitted_at).toLocaleString('en-IN') : '-'}</td>
                <td className="px-2 py-1 font-mono">****{r.card_last4 || '-'}</td>
                <td className="px-2 py-1">
                  <div className="font-medium">{r.distributor_name || '-'}</div>
                  <div className="text-[10px] text-gray-500">{r.booking_agency || ''}</div>
                </td>
                <td className="px-2 py-1 text-center">{r.rows_count}</td>
                <td className="px-2 py-1 text-right font-bold">{fmt(r.submitted_total)}</td>
                <td className="px-2 py-1 text-right text-amber-700 font-bold">{r.prebalance_used > 0 ? fmt(r.prebalance_used) : '—'}</td>
                <td className="px-2 py-1 text-right text-violet-800 font-bold">{fmt(r.card_debit)}</td>
                <td className="px-2 py-1 text-right text-[10px]">
                  <span className="text-gray-600">{fmt(r.before_balance)}</span>
                  <span className="text-gray-400"> → </span>
                  <span className={r.after_balance > 0 ? 'text-emerald-700 font-bold' : 'text-red-600 font-bold'}>{fmt(r.after_balance)}</span>
                </td>
                {!isCA && <td className="px-2 py-1">{r.submitted_by_name}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {isCA && (
        <div className="text-[10px] text-gray-500 bg-gray-50 border border-gray-200 rounded p-2">
          ℹ️ CA role: Submitter names hidden for compliance.
        </div>
      )}
    </div>
  );
}
